import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from '@sitewise/ui';
import { useMutation } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';

import {
  skipComplianceRecord,
  snoozeComplianceRecord,
  type RecordWithDeviceType,
} from '../../../api/compliance.js';
import { ApiError } from '../../../lib/api-client.js';

interface Props {
  kind: 'skip' | 'snooze';
  record: RecordWithDeviceType;
  onClose: () => void;
  onSubmitted: () => void;
}

export function SkipSnoozeDialog({ kind, record, onClose, onSubmitted }: Props) {
  const reasonId = useId();
  const daysId = useId();
  const [reason, setReason] = useState('');
  const [days, setDays] = useState(7);
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: async () => {
      if (kind === 'skip') {
        return skipComplianceRecord(record.id, { reason: reason.trim() });
      }
      return snoozeComplianceRecord(record.id, { days, reason: reason.trim() });
    },
    onSuccess: () => {
      toast.success(kind === 'skip' ? 'Record skipped.' : 'Record snoozed.');
      onSubmitted();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Could not update record');
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    submit.mutate();
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {kind === 'skip' ? 'Skip this record' : 'Snooze this record'}
          </DialogTitle>
          <DialogDescription>
            {kind === 'skip'
              ? 'Mark this required inspection as skipped (e.g. equipment removed). A future cycle will still be generated.'
              : 'Push the due date out by the chosen number of days.'}
            <span className="mt-1 block text-fg-muted">
              {record.device.label} · {record.deviceType.name}
            </span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {kind === 'snooze' ? (
            <div className="grid gap-1.5">
              <Label htmlFor={daysId}>Days</Label>
              <Input
                id={daysId}
                type="number"
                min={1}
                max={365}
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                required
              />
            </div>
          ) : null}

          <div className="grid gap-1.5">
            <Label htmlFor={reasonId}>Reason</Label>
            <textarea
              id={reasonId}
              rows={3}
              maxLength={2_000}
              required
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          {error ? (
            <div className="rounded border border-danger-subtle bg-danger-subtle/40 px-3 py-2 text-sm text-danger">
              {error}
            </div>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submit.isPending || !reason.trim()}>
              {submit.isPending ? 'Saving…' : kind === 'skip' ? 'Skip' : 'Snooze'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
